<template>
  <header class="top-navigation bg-white-100">
    <div class="nav-container">

      <div class="logo-section" @click="navigateToHome">
        <img alt="TriviumVS Lab" src="/images/logo/triviumvs-lab-logo.png" class="cursor-pointer logo-image" />
      </div>

      <nav class="nav-menu">
        <div class="nav-item" :class="{ 'nav-item-active': isActive(Urls.FEED) }" @click="navigateTo(Urls.FEED)">
          <i class="pi pi-compass nav-icon"></i>
          <span class="nav-label">Feed</span>
        </div>
        <div class="nav-item" :class="{ 'nav-item-active': isActive(Urls.BATTLES) }" @click="navigateTo(Urls.BATTLES)">
          <i class="pi pi-flag nav-icon"></i>
          <span class="nav-label">Battles</span>
        </div>
        <RoleBasedContent :roles="[UserRoles.ADMIN, UserRoles.GATEKEEPER, UserRoles.SUPERVISOR]">
          <div class="nav-item" :class="{ 'nav-item-active': isActive(Urls.COMPETITORS) }"
            @click="navigateTo(Urls.COMPETITORS)">
            <i class="pi pi-users nav-icon"></i>
            <span class="nav-label">Competitors</span>
          </div>
        </RoleBasedContent>
        <RoleBasedContent :roles="[UserRoles.ADMIN, UserRoles.GATEKEEPER, UserRoles.SUPERVISOR, UserRoles.JUDGE]">
          <div class="nav-item" :class="{ 'nav-item-active': isActive(Urls.LEADERBOARD) }"
            @click="navigateTo(Urls.LEADERBOARD)">
            <i class="pi pi-chart-bar nav-icon"></i>
            <span class="nav-label">Leaderboard</span>
          </div>
        </RoleBasedContent>
        <RoleBasedContent :roles="[UserRoles.ADMIN, UserRoles.GATEKEEPER, UserRoles.SUPERVISOR]">
          <div class="nav-item" :class="{ 'nav-item-active': isActive(Urls.JUDGES) }"
            @click="navigateTo(Urls.JUDGES)">
            <i class="pi pi-id-card nav-icon"></i>
            <span class="nav-label">Judges</span>
          </div>
        </RoleBasedContent>

        <RoleBasedContent :roles="[UserRoles.ADMIN, UserRoles.GATEKEEPER]">
          <div
            class="nav-item"
            :class="{ 'nav-item-active': isActive(Urls.ADMINISTRATION) }"
            @click="toggleAdminMenu"
            @mouseenter="showAdminMenu"
            @mouseleave="hideAdminMenuDelayed"
          >
            <i class="pi pi-cog nav-icon"></i>
            <span class="nav-label">Administration</span>
            <i class="pi pi-chevron-down nav-chevron" :class="{ 'chevron-up': adminMenuVisible }"></i>

            <div
              v-if="adminMenuVisible"
              class="admin-dropdown"
              @mouseenter="cancelHideAdminMenu"
              @mouseleave="hideAdminMenuDelayed"
            >
              <div class="dropdown-item" @click.stop="navigateToAdmin(Urls.ADMINISTRATION_USERS)">
                <i class="pi pi-users"></i>
                <span>Users</span>
              </div>
              <RoleBasedContent :roles="[UserRoles.ADMIN]">
                <div class="dropdown-item" @click.stop="navigateToAdmin(Urls.ADMINISTRATION_LOGS)">
                  <i class="pi pi-history"></i>
                  <span>Logs</span>
                </div>
                <div class="dropdown-item" @click.stop="navigateToAdmin(Urls.ADMINISTRATION_CONFIGURATION)">
                  <i class="pi pi-cog"></i>
                  <span>Configuration</span>
                </div>
              </RoleBasedContent>
            </div>
          </div>
        </RoleBasedContent>
      </nav>


      <div class="user-section">
        <div
          class="user-profile-wrapper"
          @mouseenter="showUserMenu"
          @mouseleave="hideUserMenuDelayed"
        >
          <Button
            icon="pi pi-user"
            text
            rounded
            aria-label="My account"
            class="user-profile-button"
            :class="{ 'nav-item-active': isActive(Urls.USER_PROFILE) || isActive(Urls.INFORMATION) }"
            @click="toggleUserMenu"
          />
          <Badge
            v-if="hasUnreadNotifications"
            :value="unreadCount"
            severity="danger"
            class="notification-badge"
          />

          <div
            v-if="userMenuVisible"
            class="user-dropdown"
            @mouseenter="cancelHideUserMenu"
            @mouseleave="hideUserMenuDelayed"
          >
            <div class="dropdown-item" @click="navigateToUserMenu(Urls.USER_PROFILE_PROFILE)">
              <i class="pi pi-user"></i>
              <span>Profile</span>
            </div>
            <div class="dropdown-item" @click="navigateToUserMenu(Urls.USER_PROFILE_CHANGE_PASSWORD)">
              <i class="pi pi-lock"></i>
              <span>Change password</span>
            </div>
            <div class="dropdown-item" @click="navigateToUserMenu(Urls.USER_PROFILE_THEME)">
              <i class="pi pi-palette"></i>
              <span>Theme</span>
            </div>
            <div class="dropdown-item" @click="navigateToUserMenu(Urls.USER_PROFILE_CHANGELOG)">
              <i class="pi pi-megaphone"></i>
              <span>Changelog</span>
            </div>
            <div
              class="dropdown-item"
              @click="navigateToUserMenu(Urls.USER_PROFILE_NOTIFICATIONS)"
            >
              <i class="pi pi-bell"></i>
              <span>Notifications</span>
            </div>
            <div class="dropdown-item" @click="navigateToUserMenu(Urls.INFORMATION)">
              <i class="pi pi-info-circle"></i>
              <span>Information</span>
            </div>
            <div class="dropdown-item dropdown-item--danger" @click="handleLogout">
              <i class="pi pi-sign-out"></i>
              <span>Logout</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Urls } from '@/config/Urls';
import { UserRoles } from '@/modules/admin/interfaces/UserRoles';
import { Button, Badge } from '@/components/primevue';
import RoleBasedContent from '@/components/common/RoleBasedContent/RoleBasedContent.vue';
import { useNotificationStore } from '@/stores/notificationStore';
import { useUser } from '@/modules/admin/composables/useUser';

const router = useRouter();
const route = useRoute();
const notificationStore = useNotificationStore();
const { user, logout } = useUser();

const unreadCount = computed(() => notificationStore.unreadCount);
const hasUnreadNotifications = computed(() => notificationStore.hasUnread);

const adminMenuVisible = ref(false);
const userMenuVisible = ref(false);
let hideMenuTimeout: number | null = null;
let hideUserMenuTimeout: number | null = null;

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  if (!target.closest('.nav-item')) {
    adminMenuVisible.value = false;
  }
  if (!target.closest('.user-profile-wrapper')) {
    userMenuVisible.value = false;
  }
};

watch(() => route.path, () => {
  adminMenuVisible.value = false;
  userMenuVisible.value = false;
});

let intervalId: number | null = null;

onMounted(() => {
  // Every authenticated user has an unread count (staff: shared feed;
  // others: report outcomes, mentions addressed to them).
  if (user.value?.id) {
    notificationStore.fetchUnreadCount();

    // Poll every 30 seconds for new notifications
    intervalId = window.setInterval(() => {
      notificationStore.fetchUnreadCount();
    }, 30000);
  }
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  if (intervalId !== null) {
    clearInterval(intervalId);
  }
  document.removeEventListener('click', handleClickOutside);
});

const isActive = (url?: string) => {
  if (!url) return false;
  return route.path === url || route.path.startsWith(url + '/');
};

const navigateTo = (url?: string) => {
  if (url) {
    router.push(url).catch(() => {});
  }
};

const navigateToAdmin = (path: string) => {
  adminMenuVisible.value = false;
  router.push(path).catch(() => {});
};

const navigateToHome = () => {
  const path = Urls.FEED;
  router.push(path);
};

function toggleAdminMenu() {
  adminMenuVisible.value = !adminMenuVisible.value;
}

function showAdminMenu() {
  if (hideMenuTimeout) {
    clearTimeout(hideMenuTimeout);
    hideMenuTimeout = null;
  }
  adminMenuVisible.value = true;
}

function hideAdminMenuDelayed() {
  hideMenuTimeout = window.setTimeout(() => {
    adminMenuVisible.value = false;
  }, 200);
}

function cancelHideAdminMenu() {
  if (hideMenuTimeout) {
    clearTimeout(hideMenuTimeout);
    hideMenuTimeout = null;
  }
}

function toggleUserMenu() {
  userMenuVisible.value = !userMenuVisible.value;
}

function showUserMenu() {
  if (hideUserMenuTimeout) {
    clearTimeout(hideUserMenuTimeout);
    hideUserMenuTimeout = null;
  }
  userMenuVisible.value = true;
}

function hideUserMenuDelayed() {
  hideUserMenuTimeout = window.setTimeout(() => {
    userMenuVisible.value = false;
  }, 200);
}

function cancelHideUserMenu() {
  if (hideUserMenuTimeout) {
    clearTimeout(hideUserMenuTimeout);
    hideUserMenuTimeout = null;
  }
}

function navigateToUserMenu(path: string) {
  userMenuVisible.value = false;
  router.push(path).catch(() => {});
}

function handleLogout() {
  userMenuVisible.value = false;
  logout();
  router.push(Urls.LOGIN);
}

</script>

<style scoped lang="scss">
.top-navigation {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: var(--nav-bg);
  color: var(--nav-text);
  z-index: 1000;
  box-shadow: var(--nav-shadow);
  transition: background-color 0.3s ease;
}

.nav-container {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 16px;
  max-width: 100%;
}

.logo-section {
  display: flex;
  align-items: center;
  margin-right: 32px;
  cursor: pointer;
  flex-shrink: 0;

  .logo-image {
    display: block;
    height: 35px;
    width: auto;
    object-fit: contain;
    flex-shrink: 0;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
}

/* Light theme: the white wordmark needs a dark surface — same black-chip
   treatment as ECC's light-mode nav (Trello #148 follow-up). */
[data-theme='light'] .logo-section .logo-image {
  height: 27px;
  padding: 4px 8px;
  box-sizing: content-box;
  background: #000;
  border-radius: 6px;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--nav-text);
  position: relative;

  &:hover {
    background-color: var(--nav-hover);
  }

  &.nav-item-active {
    background-color: var(--nav-active);
  }
}

.nav-chevron {
  font-size: 0.75rem;
  margin-left: auto;
  transition: transform 0.3s ease;

  &.chevron-up {
    transform: rotate(180deg);
  }
}

.admin-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 200px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.2s ease;

  i {
    font-size: 1rem;
    color: var(--accent-primary);
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }

  &:hover {
    background: var(--bg-hover);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-primary);
  }

  &--danger {
    color: var(--color-danger, #ef4444);

    i {
      color: var(--color-danger, #ef4444);
    }

    &:hover {
      background: rgba(239, 68, 68, 0.08);
    }
  }
}

.nav-icon {
  font-size: 1.1rem;
  color: var(--nav-icon) !important;
  font-family: 'primeicons' !important;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  display: inline-block;
}

.nav-label {
  font-weight: 500;
  font-size: 0.95rem;
  color: var(--nav-text);
  white-space: nowrap;
}

.user-section {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.user-profile-wrapper {
  position: relative;
  display: inline-block;
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: 700;
  z-index: 1;
}

.user-profile-button {
  font-size: 20px;
  color: var(--nav-icon) !important;

  :deep(.p-button-icon) {
    color: var(--nav-icon) !important;
  }

  &:hover {
    background-color: var(--nav-hover) !important;

    :deep(.p-button-icon) {
      color: var(--nav-text) !important;
    }
  }

  &.nav-item-active {
    background-color: var(--nav-active) !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}

// Responsive design for smaller screens
@media (max-width: 768px) {
  .nav-container {
    padding: 0 16px;
  }

  .logo-section {
    margin-right: 16px;
  }

  .nav-menu {
    gap: 4px;
  }

  .nav-item {
    padding: 8px 12px;
  }

  .nav-label {
    display: none;
  }
}

@media (max-width: 480px) {
  .nav-item {
    padding: 8px;
  }
}

@media (max-width: 360px) {
  .nav-container {
    padding: 0 8px;
  }

  .logo-section {
    margin-right: 8px;

    img {
      height: 28px;
    }
  }

  .nav-item {
    padding: 6px;
  }
}
</style>
